import axios from 'axios'
import { firebaseAuth, signOut } from '@/lib/firebase'
import { JWT_ACCESS_TOKEN_KEY } from '@/contexts/auth-context/constant'

const axiosInstance = axios.create({ baseURL: import.meta.env.VITE_API_URL })

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(JWT_ACCESS_TOKEN_KEY)
      sessionStorage.removeItem(JWT_ACCESS_TOKEN_KEY)
      delete axiosInstance.defaults.headers.common.Authorization
      signOut(firebaseAuth)
      if (window.location.pathname !== '/login') {
        window.location.replace('/login')
      }
    }

    return Promise.reject(
      (error.response && error.response.data) || 'Something went wrong!',
    )
  },
)

export default axiosInstance

export const endpoints = {
  auth: {
    login: '/auth/signin-back-office',
    me: '/users/me/profile',
  },
  profile: {
    update: '/users/me/profile',
  },
  products: {
    list: '/products',
    create: '/products',
    detail: (id: string) => `/products/${id}`,
    update: (id: string) => `/products/${id}`,
    delete: (id: string) => `/products/${id}`,
    myProducts: '/products/my-products',
  },
  articles: {
    list: '/admin/articles',
    create: '/admin/articles',
    detail: (id: string) => `/admin/articles/${id}`,
    update: (id: string) => `/admin/articles/${id}`,
    delete: (id: string) => `/admin/articles/${id}`,
  },
  users: {
    list: '/admin/users',
    create: '/admin/users',
    detail: (uid: string) => `/admin/users/${uid}`,
    update: (uid: string) => `/admin/users/${uid}`,
    delete: (uid: string) => `/admin/users/${uid}`,
    ban: (uid: string) => `/admin/users/ban/${uid}`,
  },
  orders: {
    list: '/orders',
    detail: (id: string) => `/orders/${id}`,
    update: (id: string) => `/orders/${id}`,
    delete: (id: string) => `/orders/${id}`,
    updateShippingStatus: (id: string) => `/orders/${id}/shipping-status`,
    confirmPayment: (id: string) => `/carts/${id}/confirm-payment`,
  },
  programs: {
    list: '/admin/programs',
    create: '/admin/programs',
    detail: (id: string) => `/admin/programs/${id}`,
    update: (id: string) => `/admin/programs/${id}`,
    delete: (id: string) => `/admin/programs/${id}`,
    getStreamKey: (id: string) => `/programs/${id}/generate-stream-key`,
    getToken: `/admin/programs/get-token`,
    startStreaming: (id: string) => `/admin/programs/${id}/start-streaming`,
    stopStreaming: (id: string) => `/admin/programs/${id}/stop-streaming`,
    updateFile: (id: string) => `/admin/programs/${id}/update-file`,
    logLives: '/admin/agora-live-stream-channel-webhook-payloads',
    // Automation recording disabled.
    // Keep this sample for future restore:
    // logRecords: '/admin/agora-cloud-recording-webhook-payloads',
    programRecord: '/program-records',
    programRecordById: (id: string) => `/program-records/${id}`,
    // Automation recording disabled.
    // Keep these samples for future restore:
    // cutRecord: (id: string) => `/admin/programs/${id}/cut-recording`,
    // checkRecordStatus: (id: string) =>
    //   `/admin/programs/${id}/check-recording-status`,
    // startRecording: (id: string) => `/admin/programs/${id}/start-recording`,
    // stopRecording: (id: string) => `/admin/programs/${id}/stop-recording`,
  },
  platformConfig: {
    list: '/admin/platform-configs',
    create: '/admin/platform-configs',
    detail: (id: string) => `/admin/platform-configs/${id}`,
    update: (id: string) => `/admin/platform-configs/${id}`,
    delete: (id: string) => `/admin/platform-configs/${id}`,
  },
  productCategories: {
    list: '/product-categories',
    create: '/product-categories',
    detail: (id: string) => `/product-categories/${id}`,
    update: (id: string) => `/product-categories/${id}`,
    delete: (id: string) => `/product-categories/${id}`,
  },
  feeds: {
    list: '/feeds',
    timeline: '/feeds/timeline',
    byId: (id: string, isAdmin?: boolean) =>
      `/${isAdmin ? 'admin/' : ''}feeds/${id}`,
    commentById: (id: string) => `/feeds/${id}/comments`,
    reactById: (id: string) => `/feeds/${id}/react`,
    reactionsById: (id: string) => `/feeds/${id}/reactions`,
    shareById: (id: string) => `/feeds/${id}/share`,
  },
  bankPayment: {
    list: '/admin/bank-payment-configs',
    create: '/admin/bank-payment-configs',
    detail: (id: string) => `/admin/bank-payment-configs/${id}`,
    update: (id: string) => `/admin/bank-payment-configs/${id}`,
    delete: (id: string) => `/admin/bank-payment-configs/${id}`,
    createOrUpdate: '/admin/bank-payment-configs/create-or-update',
  },
  dashboard: {
    users: (params?: Record<string, unknown>) =>
      `/dashboard/users${params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : ''}`,
    user: (uid: string) => `/dashboard/users/${uid}`,
    userStats: '/dashboard/stats/users',
    summary: '/dashboard/summary',
    monthlyStats: '/dashboard/stats/monthly',
    registerCount: (params?: Record<string, unknown>) =>
      `/dashboard/stats/register-count${params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : ''}`,
    loginCount: (params?: Record<string, unknown>) =>
      `/dashboard/stats/login-count${params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : ''}`,
  },
  privileges: {
    list: '/admin/privileges',
    create: '/admin/privileges',
    detail: (id: string) => `/admin/privileges/${id}`,
    update: (id: string) => `/admin/privileges/${id}`,
    delete: (id: string) => `/admin/privileges/${id}`,
    redeemCondition: (id: string) =>
      `/admin/privileges/${id}/redeem-conditions`,
    redeemConditionById: (id: string, conditionId: string) =>
      `/admin/privileges/${id}/redeem-conditions/${conditionId}`,
  },
  orderPrivileges: {
    list: '/admin/order-privileges',
    detail: (id: string) => `/admin/order-privileges/${id}`,
    update: (id: string) => `/admin/order-privileges/${id}`,
    updateShippingStatus: (id: string) =>
      `/admin/order-privileges/${id}/shipping`,
  },
  tokens: {
    list: '/admin/tokens',
    detail: (id: string) => `/admin/tokens/${id}`,
    transfer: '/admin/tokens/transfer',
    transferRfcFromSystem: '/admin/tokens/transfer-rfc-from-system',
  },
  userTokens: {
    list: '/admin/user-tokens',
    detail: (id: string) => `/admin/user-tokens/${id}`,
  },
  arenas: {
    list: '/admin/arenas',
    create: '/admin/arenas',
    detail: (id: string) => `/admin/arenas/${id}`,
    update: (id: string) => `/admin/arenas/${id}`,
    delete: (id: string) => `/admin/arenas/${id}`,
  },
  roosters: {
    list: '/admin/roosters',
    create: '/admin/roosters',
    detail: (id: string) => `/admin/roosters/${id}`,
    update: (id: string) => `/admin/roosters/${id}`,
    delete: (id: string) => `/admin/roosters/${id}`,
  },
  roosterMatches: {
    list: '/admin/rooster-matches',
    create: '/admin/rooster-matches',
    detail: (id: string) => `/admin/rooster-matches/${id}`,
    update: (id: string) => `/admin/rooster-matches/${id}`,
    delete: (id: string) => `/admin/rooster-matches/${id}`,
  },
  trendSounds: {
    list: '/trend-sounds',
    create: '/trend-sounds',
    detail: (id: string) => `/trend-sounds/${id}`,
    update: (id: string) => `/trend-sounds/${id}`,
    delete: (id: string) => `/trend-sounds/${id}`,
  },
  trends: {
    list: '/admin/trends',
    detail: (id: string) => `/admin/trends/${id}`,
    updateStatus: (id: string) => `/admin/trends/${id}/status`,
    updateVisibility: (id: string) => `/admin/trends/${id}/visibility`,
    delete: (id: string) => `/admin/trends/${id}`,
  },
  clips: {
    list: '/admin/clips',
    create: '/admin/clips',
    detail: (id: string) => `/admin/clips/${id}`,
    update: (id: string) => `/admin/clips/${id}`,
    delete: (id: string) => `/admin/clips/${id}`,
    updateStatus: (id: string) => `/admin/clips/${id}/status`,
    retryTranscode: (id: string) => `/admin/clips/${id}/retry-transcode`,
    comments: {
      list: (clipId: string) => `/admin/clips/${clipId}/comments`,
      create: (clipId: string) => `/admin/clips/${clipId}/comments`,
      detail: (clipId: string, commentId: string) =>
        `/admin/clips/${clipId}/comments/${commentId}`,
      update: (clipId: string, commentId: string) =>
        `/admin/clips/${clipId}/comments/${commentId}`,
      delete: (clipId: string, commentId: string) =>
        `/admin/clips/${clipId}/comments/${commentId}`,
    },
  },
  clipCategories: {
    list: '/admin/clip-categories',
    create: '/admin/clip-categories',
    update: (id: string) => `/admin/clip-categories/${id}`,
    delete: (id: string) => `/admin/clip-categories/${id}`,
    reorder: '/admin/clip-categories/reorder',
  },
} as const
